import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Layout,
  Typography,
  Form,
  Input,
  Button,
  Space,
  Avatar,
  notification,
  Row,
  Col,
  Modal,
  Tooltip,
} from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, InfoCircleOutlined } from '@ant-design/icons';
import './Login.css';

const { Header, Content, Footer } = Layout;

const Login = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 500);
    return () => clearTimeout(timer);
  }, []);

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      const response = await fetch('/backend/login.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(values),
      });

      const data = await response.json();

      if (response.ok) {
        notification.success({
          message: 'Connexion Réussie',
          description: `Bienvenue, ${values.email}!`,
          duration: 3,
        });
        navigate('/home');
        form.resetFields();
      } else {
        let errorMessage = 'Une erreur s\'est produite lors de la connexion.';
        if (data.error === 'User not found') {
          errorMessage = 'Aucun compte trouvé avec cet email.';
        } else if (data.error === 'Invalid password') {
          errorMessage = 'Mot de passe incorrect.';
        } else if (data.error) {
          errorMessage = data.error;
        }
        setModalMessage(errorMessage);
        setIsModalVisible(true);
      }
    } catch (error) {
      setModalMessage('Erreur réseau. Vérifiez votre connexion.');
      setIsModalVisible(true);
    } finally {
      setIsLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    notification.error({
      message: 'Validation échouée',
      description: 'Vérifiez vos identifiants.',
      duration: 3,
    });
  };

  const validateEmail = (_, value) => {
    if (!value) return Promise.reject(new Error('Email requis !'));
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return Promise.reject(new Error('Email invalide !'));
    }
    return Promise.resolve();
  };

  const handleModalOk = () => {
    setIsModalVisible(false);
    setModalMessage('');
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      <Header
        style={{
          background: '#ffffff',
          padding: '0 30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 15px rgba(0, 0, 0, 0.05)',
          position: 'sticky',
          top: 0,
          zIndex: 1000,
        }}
        aria-label="Header de NH Guide"
      >
        <Space align="center">
          <Avatar
            src="https://via.placeholder.com/40" // Remplace par ton logo (ex. '/logo.png')
            alt="NH Guide Logo"
            style={{ marginRight: 12, backgroundColor: '#4a90e2' }}
          />
          <Typography.Title level={4} style={{ margin: 0, color: '#4a90e2', fontWeight: 700, fontSize: 20 }}>
            NH Guide
          </Typography.Title>
        </Space>
        <div style={{ flex: 1 }} />
        <Space size={16}>
          <Button
            type="link"
            href="/register"
            style={{ color: '#333', fontWeight: 600, padding: '0 12px', fontSize: 14 }}
            onMouseEnter={(e) => (e.target.style.color = '#4a90e2')}
            onMouseLeave={(e) => (e.target.style.color = '#333')}
          >
            Register
          </Button>
          <Button
            type="link"
            href="/"
            style={{ color: '#333', fontWeight: 600, padding: '0 12px', fontSize: 14 }}
            onMouseEnter={(e) => (e.target.style.color = '#4a90e2')}
            onMouseLeave={(e) => (e.target.style.color = '#333')}
          >
            Home
          </Button>
        </Space>
      </Header>

      <Content style={{ flex: 1, padding: '40px 20px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className={`login-form-container ${isLoading ? 'loading' : ''}`}>
          <Typography.Title
            level={3}
            style={{ textAlign: 'center', color: '#2c3e50', fontWeight: 800, marginBottom: 15, fontSize: 28 }}
            aria-label="Titre de connexion"
          >
            Let's Sign in
          </Typography.Title>
          <Typography.Text
            style={{ textAlign: 'center', color: '#7f8c8d', marginBottom: 25, display: 'block', fontSize: 16, fontStyle: 'italic' }}
            aria-label="Message de bienvenue"
          >
            Welcome Back, You've been missed!
          </Typography.Text>
          <Form
            form={form}
            name="login"
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            layout="vertical"
            initialValues={{ remember: true }}
            style={{ maxWidth: '500px', width: '100%' }}
            validateTrigger="onBlur"
          >
            <Form.Item
              label="Email"
              name="email"
              rules={[{ validator: validateEmail }]}
              hasFeedback
            >
              <Input
                prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="user@gmail.com"
                size="large"
                className="custom-input"
                aria-label="Champ email"
              />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Mot de passe requis !' }]}
              hasFeedback
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
                placeholder="user123"
                size="large"
                className="custom-input"
                aria-label="Champ mot de passe"
              />
            </Form.Item>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                className="custom-button"
                style={{ width: '100%', background: 'linear-gradient(90deg, #2c3e50, #34495e)', borderColor: '#2c3e50' }}
                loading={isLoading}
                aria-label="Bouton de connexion"
              >
                Sign in
              </Button>
            </Form.Item>

            <Row justify="space-between" align="middle" style={{ marginTop: 15 }}>
              <Tooltip title="Cliquez pour réinitialiser votre mot de passe">
                <Typography.Text style={{ fontSize: 12, color: '#7f8c8d' }}>
                  <Link to="/forgot-password" style={{ color: '#4a90e2', fontWeight: 600 }}>
                    Mot de passe oublié ?
                  </Link>
                </Typography.Text>
              </Tooltip>
              <Typography.Text style={{ fontSize: 12, color: '#7f8c8d' }}>
                Don't have an account?{' '}
                <Link to="/register" style={{ color: '#4a90e2', fontWeight: 600 }}>
                  Register here
                </Link>
              </Typography.Text>
            </Row>
          </Form>
        </div>
      </Content>

      <Footer
        style={{
          textAlign: 'center',
          padding: '15px 0',
          background: '#ffffff',
          boxShadow: '0 -2px 15px rgba(0, 0, 0, 0.05)',
          color: '#7f8c8d',
          fontSize: 12,
        }}
        aria-label="Pied de page"
      >
        <Space size={12}>
          <a href="/support" style={{ color: '#4a90e2', fontWeight: 500 }}>Support</a>
          <a href="/support" style={{ color: '#4a90e2', fontWeight: 500 }}>ACHRA CoA Assistant</a>
          <a href="/privacy" style={{ color: '#4a90e2', fontWeight: 500 }}>Politique de Confidentialité</a>
          <a href="/resources" style={{ color: '#4a90e2', fontWeight: 500 }}>Ressources</a>
          <a href="/courses" style={{ color: '#4a90e2', fontWeight: 500 }}>Cours</a>
        </Space>
        <Typography.Text style={{ display: 'block', marginTop: 5, fontSize: 10 }}>
          © 2025 NH Guide. Tous droits réservés.
        </Typography.Text>
      </Footer>

      <Modal
        title="Erreur de Connexion"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalOk}
        footer={[
          <Button
            key="ok"
            type="primary"
            onClick={handleModalOk}
            style={{ background: '#4a90e2', border: 'none', borderRadius: '8px', padding: '0 20px' }}
            aria-label="Fermer la modale"
          >
            OK
          </Button>,
        ]}
        style={{ top: '20%', borderRadius: '12px', boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)' }}
        bodyStyle={{ textAlign: 'center', fontSize: '16px', color: '#2c3e50', padding: '25px' }}
        aria-label="Modale d'erreur"
      >
        <p>{modalMessage}</p>
      </Modal>
    </Layout>
  );
};

export default Login;